import { useTranslation } from "react-i18next";
import { Box, CircularProgress, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { Task, useAuth, useEntityById } from "@emberly/zenith-client";
import ResponsiveDialog from "../../../common/core/ResponsiveDialog";
import { useNavigate } from "react-router";
import MissionRequestSection from "./MissionRequestSection";
import MissionRequestStateChip from "../common/MissionRequestStateChip";
import { useCallback, useEffect, useState } from "react";
import MissionRequestResponseDialog from "../common/MissionRequestResponseDialog";
import axios from "axios";
import { useStation } from "../../../../providers/StationProvider";
import { MakeActivity } from "../../../../common/activity";
import { ActivityEnums } from "../../../../common/constants";


const useStyles = makeStyles()(
  (theme) => ({
    timeLabel: {
      color: theme.palette.text.secondary,
      fontSize: "12px"
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    },
    closeButton: {
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(1)
    },
  })
);

export default function MissionRequestDialog(props) {
  const { entityId, onClose, openButton, sent } = props;
  const { getAccessTokenSilently } = useAuth();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logEvent } = useStation();

  const { entity } = useEntityById("MissionRequest", entityId);
  const [rejectAction, setRejectAction] = useState(null);
  const [acceptAction, setAcceptAction] = useState(null);
  const [receiverStation, setReceiverStation] = useState(null);

  const onAcceptRequest = useCallback(async () => {
    try {
      const t = new Task();
      setAcceptAction(t);
      let data = await t.wait();
      
      logEvent(MakeActivity(ActivityEnums.Category.Mission, ActivityEnums.Type.MissionRequestAccepted, data.missionId, "", "", null, data.missionNumber));

      if (data.openMission) {
        setTimeout(() => {
          navigate(`/network/requests/${sent ? "sent" : "received"}/accepted/${data.missionId}/1`, { relative: "path" });
        }, 10);
      }

      onClose();

    } catch (err) {
      console.log(err);
    } finally {
      setAcceptAction(null);
    }
  }, [navigate, onClose, sent, logEvent]);

  const onRejectRequest = useCallback(async () => {
    try {
      const t = new Task();
      setRejectAction(t);
      let data = await t.wait();
      logEvent(MakeActivity(ActivityEnums.Category.Other, ActivityEnums.Type.MissionRequestRejected, null, "", "", null, data.referenceNumber));
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setRejectAction(null);
    }
  }, [onClose, logEvent]);

  useEffect(() => {
    if (!!entity && !!sent) {

      const fn = async () => {
        try {
          const res = await axios(`/api/station/network/${entity.organizationId}`, {
            headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
          });

          if (!!res.data) {
            setReceiverStation(res.data);
          }

        } catch (err) {
          console.log(err);
        }
      }

      fn();
    }
  }, [entity, sent, getAccessTokenSilently]);

  const loading = !(!!entity && (!sent || !!receiverStation));

  return (
    <ResponsiveDialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={loading ? "xs" : "lg"}
    >
      {
        !loading ? (
          <>
            <DialogTitle className={classes.titleContainer}>
              <Stack spacing={1} direction="row" alignItems="center">
                <Box>
                  {t("missionRequests:dialog:title")} {entity.referenceNumber}
                </Box>
                <MissionRequestStateChip response={entity.response} />
              </Stack>

              <IconButton onClick={onClose} size="small" color="primary" className={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <MissionRequestSection entity={entity} onAcceptRequest={onAcceptRequest} onRejectRequest={onRejectRequest} openButton={openButton} station={receiverStation} sent={sent} isPreview={sent} />
            </DialogContent>


            {!!acceptAction ? (
              <MissionRequestResponseDialog
                requestId={entity.id}
                action={acceptAction}
                accept
              />
            ) : null}

            {!!rejectAction ? (
              <MissionRequestResponseDialog
                requestId={entity.id}
                action={rejectAction}
                reject
              />
            ) : null}
          </>
        ) : (
          <Stack sx={{ height: "192px" }} alignItems="center" justifyContent="center">
            <CircularProgress variant="indeterminate" />
          </Stack>
        )
      }

    </ResponsiveDialog>
  );
}




